import React, { useEffect, useState } from "react";
import Loader from "../common/loader";
import InputField from "../element/Input";
import Select, { components } from "react-select";
import { MasterService } from "../../services";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import secureStorage from "../../config/encrypt";
import { TENANTS } from "../../config/constant";

const APP_VERSION_TENANTS = [TENANTS.OTO, TENANTS.FAV, TENANTS.TRIV, TENANTS.EMP];

const SOURCE_OPTIONS = [
    { value: "Android App", label: "Android App" },
    { value: "IOS App",     label: "IOS App" }
];

const { ValueContainer, Placeholder } = components;

const DEFAULT_FORM = {
    tenant_id:       "",
    source:          "",
    version:         "",
    launch_type:     "",
    android_app_url: "",
    ios_app_url:     "",
    message:         ""
};

const AddEditAppVersion = () => {
    const { id: versionId } = useParams();
    const isEdit = !!(versionId && parseInt(versionId) > 0);

    const [loading, setLoading]       = useState(false);
    const [formData, setFormData]     = useState(DEFAULT_FORM);
    const [errors, setErrors]         = useState({});
    const [tenantList, setTenantList] = useState([]);
    const [loginUserInfo]             = useState(secureStorage.getItem('loginUserInfo'));
    const historyLead                 = useNavigate();

    useEffect(() => {
        getTenantList();
        if (isEdit) fetchVersionDetails(versionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [versionId]);

    // ── Data fetching ────────────────────────────────────────────────────────────

    const getTenantList = async () => {
        try {
            const response = await MasterService.post("/sfa/admin/get_tenant_list", {});
            if (response && response.data && response.data.status === 200) {
                const filtered = response.data.data.filter((t) =>
                    APP_VERSION_TENANTS.includes(t.tenant_value)
                );
                setTenantList(filtered);
            }
        } catch (_) {}
    };

    const fetchVersionDetails = async (id) => {
        setLoading(true);
        try {
            const response = await MasterService.post("/sfa/admin/get_app_version_details", { id: parseInt(id) });
            if (response && response.data && response.data.status === 200) {
                const d = response.data.data;
                setFormData({
                    tenant_id:       d.tenant_id       || "",
                    source:          d.source          || "",
                    version:         d.version         || "",
                    launch_type:     d.launch_type     || "",
                    android_app_url: d.android_app_url || "",
                    ios_app_url:     d.ios_app_url     || "",
                    message:         d.message         || ""
                });
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ── Validation ───────────────────────────────────────────────────────────────

    const isFormValid = () => {
        let valid = true;
        const tempErrors = {};

        if (!formData.tenant_id || !formData.tenant_id.trim()) {
            valid = false;
            tempErrors.tenant_id = "Entity / Tenant is required";
        }
        if (!formData.source || !formData.source.trim()) {
            valid = false;
            tempErrors.source = "Platform / Source is required";
        }
        if (!formData.version || !formData.version.trim()) {
            valid = false;
            tempErrors.version = "Version is required";
        }
        if (!formData.launch_type || !formData.launch_type.trim()) {
            valid = false;
            tempErrors.launch_type = "Launch type is required";
        }

        setErrors(tempErrors);
        return valid;
    };

    // ── Event handlers ───────────────────────────────────────────────────────────

    const handleInputChange = (e, name) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleTenantChange = (option) => {
        setFormData((prev) => ({ ...prev, tenant_id: option ? option.tenant_value : "" }));
        if (errors.tenant_id) setErrors((prev) => ({ ...prev, tenant_id: "" }));
    };

    const handleSourceChange = (option) => {
        setFormData((prev) => ({ ...prev, source: option ? option.value : "" }));
        if (errors.source) setErrors((prev) => ({ ...prev, source: "" }));
    };

    const handleSave = async () => {
        if (!isFormValid()) return;

        setLoading(true);
        try {
            const payload = {
                ...formData,
                ...(isEdit
                    ? { id: parseInt(versionId), updated_by: loginUserInfo?.id || 0 }
                    : { created_by: loginUserInfo?.id || 0, updated_by: loginUserInfo?.id || 0 }
                )
            };

            const response = await MasterService.post("/sfa/admin/add_app_version", payload);
            if (response && response.data && response.data.status === 200) {
                const savedId = response.data.data?.id || versionId;
                toast.success("App version saved successfully");
                historyLead(`/view-app-version/${savedId}`);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────────

    return (
        <React.Fragment>
            <div className="add-user-outer">
                {loading ? <Loader /> : null}
                <div className="add-user-form-sec">

                    {/* ── Left panel ── */}
                    <div className="menu-tab-left-panel">
                        <div className="add-user-detail-filed">
                            <h2>App Version Details</h2>
                        </div>
                        <div className="add-user-left-panel nav">
                            <ul>
                                <li>
                                    <span className="active">
                                        <span className="img-type"></span>
                                        App Version Basic Details
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* ── Right form panel ── */}
                    <div className="route-files">
                        <div className="basic-detail-form p-xxl add-user-detail-filed">
                            <h2>App Version Basic Details</h2>

                            <h3>App Version Info</h3>
                            <div className="row">

                                {/* Entity / Tenant — Select dropdown */}
                                <fieldset className="single-select col-md-4">
                                    <div className="material">
                                        <Select
                                            components={{ ValueContainer: CustomValueContainer }}
                                            id="tenant_id"
                                            name="tenant_id"
                                            onChange={handleTenantChange}
                                            options={tenantList}
                                            value={tenantList.filter((t) => formData.tenant_id === t.tenant_value)}
                                            placeholder="Entity / Tenant *"
                                            className="react-select"
                                            classNamePrefix="react-select"
                                            getOptionValue={({ tenant_value }) => tenant_value}
                                            getOptionLabel={({ tenant_label }) => tenant_label}
                                        />
                                    </div>
                                    {errors.tenant_id && <span className="error show">{errors.tenant_id}</span>}
                                </fieldset>

                                {/* Platform / Source */}
                                <fieldset className="single-select col-md-4">
                                    <div className="material">
                                        <Select
                                            components={{ ValueContainer: CustomValueContainer }}
                                            id="source"
                                            name="source"
                                            onChange={handleSourceChange}
                                            options={SOURCE_OPTIONS}
                                            value={SOURCE_OPTIONS.filter((o) => formData.source === o.value)}
                                            placeholder="Platform / Source *"
                                            className="react-select"
                                            classNamePrefix="react-select"
                                            getOptionValue={({ value }) => value}
                                            getOptionLabel={({ label }) => label}
                                        />
                                    </div>
                                    {errors.source && <span className="error show">{errors.source}</span>}
                                </fieldset>

                                {/* Version */}
                                <fieldset className="col-md-4">
                                    <div className="material">
                                        <InputField
                                            type="text"
                                            error={errors.version || ""}
                                            name="version"
                                            id="version"
                                            label="Version"
                                            placeholder="Version"
                                            value={formData.version}
                                            onChange={(e) => handleInputChange(e, "version")}
                                            required={true}
                                        />
                                        <label data-label="Version*" className="form-label"></label>
                                    </div>
                                </fieldset>

                                {/* Launch Type */}
                                <fieldset className="col-md-4">
                                    <div className="material">
                                        <InputField
                                            type="text"
                                            error={errors.launch_type || ""}
                                            name="launch_type"
                                            id="launch_type"
                                            label="Launch Type"
                                            placeholder="Launch Type"
                                            value={formData.launch_type}
                                            onChange={(e) => handleInputChange(e, "launch_type")}
                                            required={true}
                                        />
                                        <label data-label="Launch Type* (soft / force)" className="form-label"></label>
                                    </div>
                                </fieldset>

                                {/* Android App URL */}
                                <fieldset className="col-md-4">
                                    <div className="material">
                                        <InputField
                                            type="text"
                                            error={errors.android_app_url || ""}
                                            name="android_app_url"
                                            id="android_app_url"
                                            label="Android App URL"
                                            placeholder="Android App URL"
                                            value={formData.android_app_url}
                                            onChange={(e) => handleInputChange(e, "android_app_url")}
                                        />
                                        <label data-label="Android App URL" className="form-label"></label>
                                    </div>
                                </fieldset>

                                {/* iOS App URL */}
                                <fieldset className="col-md-4">
                                    <div className="material">
                                        <InputField
                                            type="text"
                                            error={errors.ios_app_url || ""}
                                            name="ios_app_url"
                                            id="ios_app_url"
                                            label="iOS App URL"
                                            placeholder="iOS App URL"
                                            value={formData.ios_app_url}
                                            onChange={(e) => handleInputChange(e, "ios_app_url")}
                                        />
                                        <label data-label="iOS App URL" className="form-label"></label>
                                    </div>
                                </fieldset>

                                {/* Message / Release Notes */}
                                <fieldset className="col-md-8">
                                    <div className="material">
                                        <InputField
                                            type="text"
                                            error={errors.message || ""}
                                            name="message"
                                            id="message"
                                            label="Update Message"
                                            placeholder="Update Message / Release Notes"
                                            value={formData.message}
                                            onChange={(e) => handleInputChange(e, "message")}
                                        />
                                        <label data-label="Update Message / Release Notes" className="form-label"></label>
                                    </div>
                                </fieldset>

                            </div>

                            {/* Save button */}
                            <div className="br-top-flex">
                                <div className="t-right">
                                    <button type="button" className="btn-primary" onClick={handleSave}>
                                        Save & Continue
                                    </button>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </React.Fragment>
    );
};

const CustomValueContainer = ({ children, ...props }) => (
    <ValueContainer {...props}>
        <Placeholder {...props} isFocused={props.isFocused}>
            {props.selectProps.placeholder}
        </Placeholder>
        {React.Children.map(children, (child) => (child && child.type !== Placeholder ? child : null))}
    </ValueContainer>
);

export default AddEditAppVersion;
